/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { hooks, unstable, utils } from '@broadleaf/admin-components';
import { get, isEmpty } from 'lodash';

const { useCancelTokenFactory, useTracking } = hooks;
const { ContextRequest, SVG } = unstable;
const { log, RequestUtils } = utils;
const logger = log.getLogger('common.components.TaxAlertRibbon');

const { ContextParameters } = ContextRequest;
const { request } = RequestUtils;

const TaxAlertRibbon = ({ metadata, dismissed, setDismissed }) => {
  const trk = useTracking(metadata);
  const tracking = useMemo(
    () => trk,
    // eslint-disable-next-line
    [metadata]
  );
  const [getCancelToken] = useCancelTokenFactory();
  const [initialized, setInitialized] = useState(false);
  const taxConfigType = 'TAX_CONFIG';

  const dismiss = useCallback(async () => {
    setDismissed(true);
    try {
      const contextParams = {};
      contextParams[ContextParameters.TRACKING] = tracking;
      await request(
        {
          method: 'POST',
          uri: `/tenant/dealer-acknowledgement/${taxConfigType}`,
          data: { value: true, isPrivate: false },
          scope: 'DEALER_APPLICATION',
          cancelToken: getCancelToken()
        },
        contextParams
      );
    } catch (err) {
      if (err.cancelled) {
        return;
      }
      logger.error(
        'Encountered error trying to save dismissal of tax alert',
        err
      );
    }
    // eslint-disable-next-line
  }, [tracking]);

  useEffect(() => {
    (async () => {
      const contextParams = {};
      contextParams[ContextParameters.TRACKING] = tracking;

      try {
        const caveatResponse = await request(
          {
            method: 'GET',
            uri: `/tenant/dealer-acknowledgement/${taxConfigType}`,
            scope: 'DEALER_APPLICATION',
            cancelToken: getCancelToken()
          },
          contextParams
        );
        const dismissedPreviously = caveatResponse.data !== null;
        setInitialized(true);

        if (dismissedPreviously) {
          setDismissed(true);
          return;
        }
      } catch (err) {
        if (!err.cancelled) {
          logger.error(
            'Encountered error trying to read previous tax acknowledgement',
            err
          );
        }
      }

      try {
        const taxResponse = await request(
          {
            method: 'GET',
            uri: '/cart/dealer-tax-configs',
            cancelToken: getCancelToken()
          },
          contextParams
        );
        const avatax = !isEmpty(
          get(taxResponse, 'data.avalaraTaxConfig.companyCode')
        );
        const simple = !isEmpty(get(taxResponse, 'data.simpleTaxConfig.id'));
        setDismissed(avatax || simple);
      } catch (err) {
        if (!err.cancelled) {
          logger.error('Encountered error trying to read tax configs', err);
        }
      }
    })();
    // eslint-disable-next-line
  }, [tracking]);

  if (dismissed || !initialized) {
    return null;
  }

  return (
    <aside className="items-center tw-flex tw-w-full tw-justify-center tw-bg-red-200 tw-px-2 tw-py-1 tw-font-semibold tw-text-red-700 md:tw-justify-end md:tw-px-4">
      <div className="tw-inline-flex tw-items-center tw-p-2 tw-text-sm">
        <SVG
          className="tw-mr-2 tw-inline tw-h-4 tw-w-4 tw-fill-current"
          name="exclamation-outline"
        />
      </div>
      <button
        className="focus:tw-shadow-outline tw-my-1 tw-flex tw-inline-flex tw-appearance-none tw-items-center tw-rounded tw-bg-red-600 tw-px-4 tw-py-2 tw-text-sm tw-font-bold tw-text-white tw-shadow hover:tw-bg-red-700 focus:tw-bg-red-700 focus:tw-outline-none"
        onClick={() => {
          dismiss();
        }}
      >
        <span>I acknowledge</span>
      </button>
      <div className="tw-inline-flex tw-items-center tw-p-2 tw-text-sm">
        <span>
          I must configure the tax functionality in order for taxes to be
          collected as part of customer checkout
        </span>
      </div>
    </aside>
  );
};

export default TaxAlertRibbon;
export { TaxAlertRibbon };
